import { defineStore, storeToRefs } from "pinia";
import { baseSuccessToastMessage } from "@/primevue/init";
import { computed, ref } from "vue";
import { useToast } from "primevue/usetoast";
import { api, types } from "@/lib/openapi";
import {
    useWorktreeManager,
    useWorktreeStore,
} from "@/composables/worktree-manager";
import { useCurrentEntityManager } from "./entity-store";

export type ProductInTree = types.ProductInTree;

export const useProductsStore = defineStore("products", () => {
    const { sync } = useWorktreeStore();
    const { isFetching: isWorktreeBeingFetched } = useWorktreeManager();
    const { domain, isFetching: isEntityBeingFetched } =
        useCurrentEntityManager();
    const toast = useToast();
    const isLoading = ref(false);

    // Note, we're not fetching the products, we're using the worktree, which
    // also contains the products (just less detailed), to avoid another
    // fetch.
    const products = computed<ProductInTree[]>(() => {
        if (!domain.value) {
            return [];
        }

        return domain.value.products.sort((a, b) =>
            a.name.localeCompare(b.name)
        );
    });

    /**
     * Create a new product. When no domainId is provided,
     * uses the current domain.
     */
    const create = async (
        name: string,
        domainId?: string,
        opts: { showToast: boolean } = { showToast: true },
        description?: Blob
    ) => {
        isLoading.value = true;
        const { data, response } =
            await api.startProductProductMgtCreateProductStartPost({
                body: {
                    domain_id: domainId ?? domain.value?.identity ?? "",
                    name: name,
                    description: description,
                },
            });

        if (response.ok) {
            if (opts.showToast) {
                toast.add({
                    ...baseSuccessToastMessage,
                    summary: `Product / service ${name} created`,
                });
            }
            await sync();
        } else {
            if (opts.showToast) {
                toast.add({
                    severity: "error",
                    summary: "Could not create product / service",
                    detail: "Server returned " + response.status,
                });
            }
        }
        isLoading.value = false;
        return data;
    };

    /**
     * Deletes the given Product.
     */
    async function remove(
        productId: string,
        opts: { showToast: boolean } = { showToast: true }
    ) {
        isLoading.value = true;
        const { response } =
            await api.startProductProductMgtDeleteProductStartPost({
                body: {
                    product_id: productId,
                },
            });

        if (response.ok) {
            if (opts.showToast) {
                toast.add({
                    ...baseSuccessToastMessage,
                    summary: `Product / service ${productId} deleted`,
                });
            }
            await sync();
        } else {
            if (opts.showToast) {
                toast.add({
                    severity: "error",
                    summary: "Could not delete product / service: " + productId,
                    detail: "Server returned " + response.status,
                });
            }
        }
        isLoading.value = false;
    }

    return {
        create,
        remove,
        products,
        isLoading,
        isFetching: computed(
            () => isWorktreeBeingFetched.value || isEntityBeingFetched.value
        ),
    };
});

export const useProductsManager = () => storeToRefs(useProductsStore());
